import { useEffect, useMemo, useState } from "react";
import { RoadmapResearchPanel } from "@/components/admin/RoadmapResearchPanel";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import UsersTable from "@/components/admin/UsersTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  CreditCard,
  ClipboardCheck,
  TrendingUp,
  IndianRupee,
  RefreshCw,
  Activity,
  Ticket,
  BarChart3,
  Wrench,
  ArrowUpRight,
  Search,
  Gift,
  Mail,
} from "lucide-react";

type Kpis = {
  totalUsers: number;
  paidUsers: number;
  assessmentsCompleted: number;
  assessmentsInProgress: number;
  revenueInr: number;
  signups7d: number;
  payments7d: number;
  completions7d: number;
  conversionPct: number;
};

type RecentUser = {
  id: string;
  email: string | null;
  full_name: string | null;
  created_at: string;
};
type RecentPayment = {
  id: string;
  user_id: string | null;
  final_price: number | null;
  currency: string | null;
  payment_status: string | null;
  payment_provider: string | null;
  created_at: string;
  email?: string | null;
};

type RecentAssessment = {
  id: string;
  user_id: string;
  status: string;
  age_group: string | null;
  created_at: string;
  updated_at: string;
  email?: string | null;
};

const fmtINR = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const statusVariant = (s?: string | null) => {
  switch ((s || "").toLowerCase()) {
    case "success":
    case "completed":
    case "scored":
    case "paid":
      return "default" as const;
    case "in_progress":
    case "pending":
    case "created":
      return "secondary" as const;
    case "failed":
    case "refunded":
    case "partially_refunded":
    case "disputed":
      return "destructive" as const;
    default:
      return "outline" as const;
  }
};

const AdminHub = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [payments, setPayments] = useState<RecentPayment[]>([]);
  const [assessments, setAssessments] = useState<RecentAssessment[]>([]);
  const [query, setQuery] = useState("");
  const [pulling, setPulling] = useState(false);
  const currentCommit = (typeof __APP_COMMIT__ !== "undefined" ? __APP_COMMIT__ : "") as string;
  const [lastPull, setLastPull] = useState<{ commit: string; at: string } | null>(() => {
    try {
      const raw = localStorage.getItem("admin:lastGitPull");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const handlePullFromGit = async () => {
    setPulling(true);
    const toastId = toast.loading("Pulling latest from Git…");
    try {
      // Probe the app shell to confirm the latest build is reachable before reloading.
      const res = await fetch(`/?_pull=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const record = { commit: currentCommit, at: new Date().toISOString() };
      localStorage.setItem("admin:lastGitPull", JSON.stringify(record));
      setLastPull(record);
      toast.success("Latest build reachable — reloading…", { id: toastId });
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      console.error("[AdminHub] Pull from Git failed", err);
      toast.error("Pull failed. Please try again.", {
        id: toastId,
        description: err instanceof Error ? err.message : String(err),
      });
      setPulling(false);
    }
  };


  const load = async () => {
    setLoading(true);
    const sevenDays = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();

    const [
      profilesCount,
      paidDistinct,
      doneCount,
      progressCount,
      revenue,
      signups7,
      pay7,
      done7,
      recentProfiles,
      recentPayments,
      recentAssessments,
    ] = await Promise.all([
      supabase.from("profiles").select("id", { count: "exact", head: true }),
      supabase
        .from("payments")
        .select("user_id", { count: "exact", head: false })
        .eq("payment_status", "success"),
      supabase.from("assessments").select("id", { count: "exact", head: true }).eq("status", "completed"),
      supabase.from("assessments").select("id", { count: "exact", head: true }).eq("status", "in_progress"),
      supabase.from("payments").select("final_price").eq("payment_status", "success"),
      supabase.from("profiles").select("id", { count: "exact", head: true }).gte("created_at", sevenDays),
      supabase
        .from("payments")
        .select("id", { count: "exact", head: true })
        .eq("payment_status", "success")
        .gte("created_at", sevenDays),
      supabase
        .from("assessments")
        .select("id", { count: "exact", head: true })
        .eq("status", "completed")
        .gte("updated_at", sevenDays),
      supabase
        .from("profiles")
        .select("id,email,full_name,created_at")
        .order("created_at", { ascending: false })
        .limit(25),
      supabase
        .from("payments")
        .select("id,user_id,final_price,currency,payment_status,payment_provider,created_at")
        .order("created_at", { ascending: false })
        .limit(25),
      supabase
        .from("assessments")
        .select("id,user_id,status,age_group,created_at,updated_at")
        .order("updated_at", { ascending: false })
        .limit(25),
    ]);

    const totalUsers = profilesCount.count ?? 0;
    const paidUsers = new Set((paidDistinct.data ?? []).map((r: any) => r.user_id)).size;
    const revenueInr = (revenue.data ?? []).reduce((s: number, r: any) => s + Number(r.final_price || 0), 0);

    setKpis({
      totalUsers,
      paidUsers,
      assessmentsCompleted: doneCount.count ?? 0,
      assessmentsInProgress: progressCount.count ?? 0,
      revenueInr,
      signups7d: signups7.count ?? 0,
      payments7d: pay7.count ?? 0,
      completions7d: done7.count ?? 0,
      conversionPct: totalUsers ? Math.round((paidUsers / totalUsers) * 1000) / 10 : 0,
    });

    // Hydrate emails for payments + assessments
    const userIds = new Set<string>();
    (recentPayments.data ?? []).forEach((p: any) => p.user_id && userIds.add(p.user_id));
    (recentAssessments.data ?? []).forEach((a: any) => a.user_id && userIds.add(a.user_id));
    let emailMap = new Map<string, string>();
    if (userIds.size) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id,email")
        .in("id", Array.from(userIds));
      (profs ?? []).forEach((p: any) => emailMap.set(p.id, p.email));
    }

    setPayments(
      ((recentPayments.data ?? []) as RecentPayment[]).map((p) => ({
        ...p,
        email: p.user_id ? emailMap.get(p.user_id) ?? null : null,
      })),
    );
    setAssessments(
      ((recentAssessments.data ?? []) as RecentAssessment[]).map((a) => ({
        ...a,
        email: emailMap.get(a.user_id) ?? null,
      })),
    );

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);


  const filteredPayments = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter(
      (p) =>
        (p.email ?? "").toLowerCase().includes(q) ||
        (p.id ?? "").toLowerCase().includes(q) ||
        (p.payment_status ?? "").toLowerCase().includes(q),
    );
  }, [payments, query]);

  const filteredAssessments = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assessments;
    return assessments.filter(
      (a) =>
        (a.email ?? "").toLowerCase().includes(q) ||
        (a.status ?? "").toLowerCase().includes(q),
    );
  }, [assessments, query]);

  return (
    <div className="min-h-screen bg-background">
      <PageHead
        title="Admin Hub | WeKIT™"
        description="Central admin dashboard for WeKIT — users, payments, assessments, analytics."
        path="/admin"
        noIndex
      />

      <header className="border-b">
        <div className="container py-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">WeKIT™ control center</p>
            <h1 className="text-3xl font-bold tracking-tight">Admin Hub</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Everything in one place — users, payments, assessments, analytics.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={pulling}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${pulling ? "animate-spin" : ""}`} />
                  {pulling ? "Pulling…" : "Pull from Git"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Pull latest from Git?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reload the app to pick up the latest build synced from GitHub.
                    Users currently on the page may see a brief interruption while the new
                    bundle loads. In-flight admin actions could be lost.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handlePullFromGit}>
                    Pull &amp; reload
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
        <div className="container pb-4 -mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
          <span>
            Current build:{" "}
            <span className="font-mono">
              {currentCommit ? currentCommit.slice(0, 7) : "unknown"}
            </span>
          </span>
          <span>
            Last Git pull:{" "}
            {lastPull ? (
              <>
                <span className="font-mono">{lastPull.commit.slice(0, 7) || "unknown"}</span>
                {" · "}
                {fmtDate(lastPull.at)}
              </>
            ) : (
              "never"
            )}
          </span>
        </div>
      </header>

      <main className="container py-8 space-y-8">
        {/* KPIs */}
        <section aria-label="Key metrics" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            icon={<Users className="h-5 w-5" />}
            label="Total users"
            value={kpis ? kpis.totalUsers.toLocaleString() : "—"}
            sub={kpis ? `+${kpis.signups7d} this week` : ""}
          />
          <KpiCard
            icon={<CreditCard className="h-5 w-5" />}
            label="Paid users"
            value={kpis ? kpis.paidUsers.toLocaleString() : "—"}
            sub={kpis ? `${kpis.conversionPct}% conversion` : ""}
          />
          <KpiCard
            icon={<ClipboardCheck className="h-5 w-5" />}
            label="Assessments completed"
            value={kpis ? kpis.assessmentsCompleted.toLocaleString() : "—"}
            sub={kpis ? `+${kpis.completions7d} this week` : ""}
          />
          <KpiCard
            icon={<IndianRupee className="h-5 w-5" />}
            label="Revenue (₹)"
            value={kpis ? fmtINR(kpis.revenueInr) : "—"}
            sub={kpis ? `+${kpis.payments7d} payments this week` : ""}
          />
        </section>

        {/* Quick links */}
        <section aria-label="Admin tools" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickLink to="/admin/payments" icon={<CreditCard className="h-4 w-4" />} label="Payments" />
          <QuickLink to="/admin/payment-console" icon={<Wrench className="h-4 w-4" />} label="Payment Console" />
          <QuickLink to="/admin/webhook-log" icon={<Activity className="h-4 w-4" />} label="Webhook Log" />
          <QuickLink to="/admin/analytics" icon={<BarChart3 className="h-4 w-4" />} label="Analytics" />
          <QuickLink to="/admin/pricing-analytics" icon={<IndianRupee className="h-4 w-4" />} label="Pricing Analytics" />
          <QuickLink to="/admin/invites" icon={<Ticket className="h-4 w-4" />} label="Comp Invites" />
          <QuickLink to="/admin/benefits" icon={<Gift className="h-4 w-4" />} label="Benefits & Coupons" />
          <QuickLink to="/admin/access-benefits" icon={<Gift className="h-4 w-4" />} label="Access & Benefits" />
          <QuickLink to="/admin/scholarships" icon={<Gift className="h-4 w-4" />} label="Scholarship approvals" />
          <QuickLink to="/admin/coupons" icon={<Ticket className="h-4 w-4" />} label="Coupons" />
          <QuickLink to="/admin/launch-offer" icon={<Ticket className="h-4 w-4" />} label="Launch Offer" />
          <QuickLink to="/admin/audit-log" icon={<Activity className="h-4 w-4" />} label="Audit Log" />
          <QuickLink to="/admin/coupons/history" icon={<Activity className="h-4 w-4" />} label="Coupon Audit" />
          <QuickLink to="/admin/waitlists" icon={<Users className="h-4 w-4" />} label="Waitlists" />
          <QuickLink to="/admin/reports" icon={<Users className="h-4 w-4" />} label="Learner reports" />
          <QuickLink to="/admin/colleges" icon={<Users className="h-4 w-4" />} label="Colleges" />
          <QuickLink to="/admin/demo-institution" icon={<Wrench className="h-4 w-4" />} label="Demo Branding" />
          <QuickLink to="/admin/evidence-governance" icon={<Wrench className="h-4 w-4" />} label="Evidence Governance™" />
          <QuickLink to="/admin/email-log" icon={<Mail className="h-4 w-4" />} label="Email Delivery Log" />
          <QuickLink to="/admin/notifications/health" icon={<Activity className="h-4 w-4" />} label="Notifications Health" />
          <QuickLink to="/admin/flags" icon={<Wrench className="h-4 w-4" />} label="Feature Flags" />
          <QuickLink to="/admin/backend" icon={<Wrench className="h-4 w-4" />} label="Backend Ops" />
          <QuickLink to="/admin/observability" icon={<Activity className="h-4 w-4" />} label="Observability" />
        </section>

        {/* What we shipped — Principal / Institution experience */}
        <section aria-label="Principal experience" className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Principal &amp; Institution experience</h2>
            <p className="text-sm text-muted-foreground">
              Everything built for the Meeting Mode &amp; Executive Brief flow. Open in a new tab to preview.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <ShippedLink
              to="/meeting"
              title="Meeting Mode — Cinematic Welcome"
              desc="5s branded intro using the active demo profile, then routes into /institution."
            />
            <ShippedLink
              to="/institution"
              title="Institution Dashboard"
              desc="Executive Brief with drill-downs, timestamp, empty states, and Recommended Actions."
            />
            <ShippedLink
              to="/institution/intelligence"
              title="Institution Intelligence"
              desc="Deeper analytics view with #celebrations anchor for grade-level highlights."
            />
            <ShippedLink
              to="/institution/brief/print"
              title="Morning Briefing (print / PDF)"
              desc="Print-friendly executive summary — Cmd/Ctrl+P to save as PDF, or Copy summary."
              newTab
            />
            <ShippedLink
              to="/institution/audit"
              title="Institution Audit"
              desc="Audit log for institution admin actions."
            />
            <ShippedLink
              to="/admin/demo-institution"
              title="Demo Branding editor"
              desc="Configure school name, principal, colors, logo — with validation before activation."
            />
            <ShippedLink
              to="/admin/notifications/health"
              title="Notifications Health"
              desc="Dead-letter filters by user / token status / timestamp + Send test notification."
            />
          </div>
        </section>

        {/* Default Demo Principal — permanent login for every school demo */}
        <section aria-label="Demo Principal" className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Default Demo Principal</h2>
            <p className="text-sm text-muted-foreground">
              Permanent login for school demos, webinars, and investor meetings. Identity adapts to the active
              Demo Branding profile — credentials never change.
            </p>
          </div>
          <div className="rounded-2xl border bg-card p-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Email</p>
                <p className="font-mono text-sm">principal@demo.wekit.ai</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Password</p>
                <p className="font-mono text-sm">welcome2clarity</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Role</p>
                <p className="text-sm">Institution Admin · WeKIT Demo School (default)</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Landing</p>
                <p className="text-sm">Sign in → /meeting → /institution</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to="/admin/demo-institution"
                className="inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Switch Demo Profile
              </Link>
              <a
                href="/auth"
                className="inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Open sign-in page
              </a>
            </div>
          </div>
        </section>




        {/* Search */}
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter by email, name, status, or ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-11"
          />
        </div>

        {/* Activity tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users">Recent users</TabsTrigger>
            <TabsTrigger value="payments">Recent payments</TabsTrigger>
            <TabsTrigger value="assessments">Recent assessments</TabsTrigger>
            <TabsTrigger value="roadmaps">Country roadmaps</TabsTrigger>
          </TabsList>

          <TabsContent value="roadmaps">
            <RoadmapResearchPanel />
          </TabsContent>

          <TabsContent value="users">
            <UsersTable currentUserId={user?.id} />
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Latest payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <SkeletonRows />
                ) : filteredPayments.length === 0 ? (
                  <EmptyState label="No payments match your filter." />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>When</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPayments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.email ?? "—"}</TableCell>
                          <TableCell>
                            {p.currency === "INR" || !p.currency
                              ? fmtINR(Number(p.final_price || 0))
                              : `${p.currency} ${p.final_price}`}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(p.payment_status)}>{p.payment_status ?? "—"}</Badge>
                          </TableCell>
                          <TableCell className="uppercase text-xs text-muted-foreground">
                            {p.payment_provider ?? "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{fmtDate(p.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="assessments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" /> Latest assessments
                  <span className="ml-auto text-xs text-muted-foreground font-normal">
                    {kpis ? `${kpis.assessmentsInProgress} currently in progress` : ""}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <SkeletonRows />
                ) : filteredAssessments.length === 0 ? (
                  <EmptyState label="No assessments match your filter." />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Age group</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Last activity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAssessments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{a.email ?? "—"}</TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(a.status)}>{a.status}</Badge>
                          </TableCell>
                          <TableCell>{a.age_group ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                          <TableCell className="text-muted-foreground">{fmtDate(a.updated_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <section className="grid gap-4 md:grid-cols-3">
          <SubKpi icon={<TrendingUp className="h-4 w-4" />} label="Signups (7d)" value={kpis?.signups7d ?? "—"} />
          <SubKpi icon={<CreditCard className="h-4 w-4" />} label="Payments (7d)" value={kpis?.payments7d ?? "—"} />
          <SubKpi
            icon={<ClipboardCheck className="h-4 w-4" />}
            label="Completions (7d)"
            value={kpis?.completions7d ?? "—"}
          />
        </section>
      </main>
    </div>
  );
};

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="rounded-3xl">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between text-muted-foreground text-sm">
          <span>{label}</span>
          <span className="text-primary">{icon}</span>
        </div>
        <div className="mt-2 text-3xl font-bold tracking-tight">{value}</div>
        {sub ? <div className="mt-1 text-xs text-muted-foreground">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

function ShippedLink({
  to,
  title,
  desc,
  newTab,
}: {
  to: string;
  title: string;
  desc: string;
  newTab?: boolean;
}) {
  const props = newTab ? { target: "_blank", rel: "noreferrer" as const } : {};
  return (
    <Link
      to={to}
      {...props}
      className="group rounded-2xl border bg-card p-4 hover:bg-muted/40 transition block"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold">{title}</div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition shrink-0" />
      </div>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{desc}</p>
      <div className="mt-2 text-[11px] font-mono text-muted-foreground">{to}</div>
    </Link>
  );
}


function SubKpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card className="rounded-2xl">
      <CardContent className="py-4 flex items-center gap-3">
        <span className="text-primary">{icon}</span>
        <div className="flex-1">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-lg font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-2xl border bg-card px-4 py-3 hover:bg-muted/40 transition"
    >
      <span className="flex items-center gap-2 text-sm font-medium">
        {icon}
        {label}
      </span>
      <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition" />
    </Link>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-10 rounded-md bg-muted/50 animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return <p className="text-sm text-muted-foreground py-8 text-center">{label}</p>;
}

export default AdminHub;
